import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UpsellPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storedSessionId = localStorage.getItem('creditSessionId');
  const sessionId = location.state?.sessionId || storedSessionId;
  const fromBeta = location.state?.fromBeta;
  const planData = location.state?.planData || {};
  
  const [loading, setLoading] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [showBureauUpload, setShowBureauUpload] = useState(false);
  const [bureauToUpload, setBureauToUpload] = useState(null); // 'one' or 'both'
  const [uploadedBureaus, setUploadedBureaus] = useState([]);
  const [betaCode, setBetaCode] = useState('');
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaError, setBetaError] = useState('');
  const [proUnlocked, setProUnlocked] = useState(false);
  
  // Get current report source
  const reportSource = localStorage.getItem('reportSource') || 'experian';

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }
    // Auto-unlock plan on upsell page mount
    const betaCodeStored = localStorage.getItem('betaCode');
    if (betaCodeStored && sessionId) {
      axios.post(`${API}/beta-unlock`, {
        session_id: sessionId,
        beta_code: betaCodeStored
      }).then(() => {
        console.log('Plan auto-unlocked on upsell page');
      }).catch(() => {
        // Silently handle — plan may already be unlocked or code may be invalid
      });
    }
  }, [sessionId, navigate]);

  const handleUpgradeSelect = async (option) => {
    try {
      setSelectedUpgrade(option);
      
      if (option === 'continue') {
        localStorage.setItem('creditSessionId', sessionId);
        navigate('/dashboard', { state: { sessionId, planData } });
      } else if (option === 'one-bureau') {
        setBureauToUpload('one');
        setShowBureauUpload(true);
      } else if (option === 'all-bureaus') {
        setBureauToUpload('both');
        setShowBureauUpload(true);
      }
    } catch (err) {
      console.error('Upgrade select error:', err);
    }
  };

  const handleBureauUpload = async (e, bureau) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      return;
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bureau', bureau);
      formData.append('session_id', sessionId);

      // Upload additional bureau
      await axios.post(`${API}/upload-additional-bureau`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadedBureaus(prev => [...prev, bureau]);
      
      // Check if we have all required bureaus
      const requiredCount = bureauToUpload === 'both' ? 2 : 1;
      if (uploadedBureaus.length + 1 >= requiredCount) {
        // Recalculate plan with new data
        setLoading(true);
        await axios.post(`${API}/recalculate-plan`, { session_id: sessionId });
        
        localStorage.setItem('creditSessionId', sessionId);
        localStorage.setItem('bureauCount', (uploadedBureaus.length + 2).toString()); // +1 original +1 new
        navigate('/dashboard', { state: { sessionId, upgraded: true, bureauCount: uploadedBureaus.length + 2 } });
      }
    } catch (error) {
      console.error('Bureau upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBetaUpgrade = async () => {
    if (!betaCode.trim()) {
      setBetaError('Please enter a beta code');
      return;
    }
    
    setBetaLoading(true);
    setBetaError('');
    
    try {
      const response = await axios.post(`${API}/beta-pro-unlock`, {
        session_id: sessionId,
        beta_code: betaCode.trim()
      });
      
      if (response.data.success) {
        setProUnlocked(true);
        localStorage.setItem('creditSessionId', sessionId);
        localStorage.setItem('hasProAccess', 'true');
        
        setTimeout(() => {
          navigate('/dashboard', { state: { sessionId, upgraded: true, fromBeta: true } });
        }, 1500);
      }
    } catch (error) {
      setBetaError(error.response?.data?.detail || 'Invalid beta code');
    } finally {
      setBetaLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('creditSessionId', sessionId);
    navigate('/dashboard', { state: { sessionId, planData } });
  };

  const [downloadError, setDownloadError] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadPlan = async () => {
    if (!sessionId) return;
    setDownloadLoading(true);
    setDownloadError('');
    const url = `${API}/plan/${sessionId}/download`;
    
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'Fast-Track-Score-Boost-Plan.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        setDownloadLoading(false);
        return;
      } catch (err) {
        console.error(`Download attempt ${attempt}:`, err);
        if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
      }
    }
    // Final fallback: open in new tab
    try { window.open(url, '_blank'); } catch {}
    setDownloadLoading(false);
  };

  // Bureau upload modal
  if (showBureauUpload) {
    const needsTransUnion = bureauToUpload === 'both' && !uploadedBureaus.includes('transunion');
    const needsEquifax = bureauToUpload === 'both' ? !uploadedBureaus.includes('equifax') : !uploadedBureaus.includes('additional');
    
    return (
      <div className="upsell-container">
        <div className="upsell-box bureau-upload-box">
          <button 
            className="back-btn"
            onClick={() => setShowBureauUpload(false)}
          >
            ← Back
          </button>
          
          <h1 className="bureau-upload-title">
            {bureauToUpload === 'both' ? 'Upload Additional Reports' : 'Upload Additional Report'}
          </h1>
          <p className="bureau-upload-subtitle">
            Add more bureaus to improve your plan accuracy
          </p>

          <div className="bureau-upload-cards">
            {bureauToUpload === 'both' ? (
              <>
                {/* TransUnion Upload */}
                <div className={`bureau-card ${uploadedBureaus.includes('transunion') ? 'uploaded' : ''}`}>
                  <div className="bureau-header">
                    <span className="bureau-name">TransUnion</span>
                    {uploadedBureaus.includes('transunion') && <span className="uploaded-badge">✓ Uploaded</span>}
                  </div>
                  {!uploadedBureaus.includes('transunion') && (
                    <label className="bureau-upload-btn">
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => handleBureauUpload(e, 'transunion')}
                        disabled={loading}
                      />
                      {loading ? 'Uploading...' : 'Upload TransUnion PDF'}
                    </label>
                  )}
                </div>

                {/* 3-Bureau Visual Reference */}
                <div className="bureau-visual-reference">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_677e7a8a-aea4-44eb-9785-abfbfb7cf099/artifacts/war4rwbh_IMG_3945.jpeg"
                    alt="3-Bureau Credit Analysis Preview"
                    className="bureau-reference-img"
                    data-testid="bureau-reference-image"
                  />
                </div>

                {/* Equifax Upload */}
                <div className={`bureau-card ${uploadedBureaus.includes('equifax') ? 'uploaded' : ''}`}>
                  <div className="bureau-header">
                    <span className="bureau-name">Equifax</span>
                    {uploadedBureaus.includes('equifax') && <span className="uploaded-badge">✓ Uploaded</span>}
                  </div>
                  {!uploadedBureaus.includes('equifax') && (
                    <label className="bureau-upload-btn">
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={(e) => handleBureauUpload(e, 'equifax')}
                        disabled={loading}
                      />
                      {loading ? 'Uploading...' : 'Upload Equifax PDF'}
                    </label>
                  )}
                </div>
              </>
            ) : (
              /* Single Bureau Upload */
              <div className="bureau-card">
                <div className="bureau-header">
                  <span className="bureau-name">TransUnion or Equifax</span>
                </div>
                <p className="bureau-hint">Upload either one to enhance your plan</p>
                <label className="bureau-upload-btn">
                  <input
                    type="file"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => handleBureauUpload(e, 'additional')}
                    disabled={loading}
                  />
                  {loading ? 'Uploading...' : 'Upload Report PDF'}
                </label>
              </div>
            )}
          </div>

          <p className="bureau-help-text">
            Get your free reports from <a href="https://www.annualcreditreport.com" target="_blank" rel="noopener noreferrer">AnnualCreditReport.com</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="upsell-container">
      <div className="upsell-box">
        {/* Success Badge */}
        <div className="upsell-success-badge">
          <span className="success-icon">✓</span>
          <span>{proUnlocked ? 'Pro Access Activated!' : 'Payment Successful!'}</span>
        </div>

        {/* Plan Generated Confirmation */}
        <div className="plan-generated-section">
          <p className="plan-generated-text">Your personalized credit action plan is ready!</p>
          <button 
            className="download-plan-btn"
            onClick={handleDownloadPlan}
            disabled={downloadLoading}
            data-testid="download-plan-btn"
          >
            {downloadLoading ? 'Downloading...' : 'Download Your Action Plan (PDF)'}
          </button>
        </div>

        {proUnlocked ? (
          <div className="upsell-header">
            <h1 className="upsell-title">Welcome to Credit GPS Pro!</h1>
            <p className="upsell-subtitle">
              Your pro features have been unlocked. Redirecting to your dashboard...
            </p>
            <div style={{ marginTop: 24 }}>
              <div className="spinner"></div>
            </div>
          </div>
        ) : (
          <>
            {/* Upgrade Header */}
            <div className="upsell-header">
              <h1 className="upsell-title">Want a More Complete & Accurate Plan?</h1>
              <p className="upsell-subtitle">
                Different lenders use different credit bureaus. Add more data to improve your results.
              </p>
            </div>

            {/* Trust Statement */}
            <div className="plan-trust-statement">
              <span className="trust-icon">✔</span>
              <p>Your plan is based on your {reportSource === 'experian' ? 'Experian' : 'uploaded'} credit report.</p>
            </div>

            {/* 3-Bureau Visual Reference */}
            <div className="upsell-reference-image" data-testid="upsell-reference-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_677e7a8a-aea4-44eb-9785-abfbfb7cf099/artifacts/fpzqnlam_IMG_4203.png"
                alt="Upgrade to 3-Bureau Action Plan"
                style={{ width: '100%', maxWidth: 420, borderRadius: 16, display: 'block', margin: '0 auto' }}
              />
            </div>

            {/* Three Upgrade Options */}
            <div className="upgrade-cards">
              {/* Option 1: Keep Current */}
              <div 
                className={`upgrade-card ${selectedUpgrade === 'continue' ? 'selected' : ''}`}
                onClick={() => handleUpgradeSelect('continue')}
                data-testid="upgrade-continue"
              >
                <div className="upgrade-card-header">
                  <h3 className="upgrade-name">Keep Current Plan</h3>
                </div>
                <div className="upgrade-price">
                  <span className="price-add">+$0</span>
                  <span className="price-total">$29.99 total</span>
                </div>
                <p className="upgrade-desc">Continue with your single-bureau plan</p>
                <button className="upgrade-card-btn">
                  Continue
                </button>
              </div>

              {/* Option 2: Add 1 Bureau */}
              <div 
                className={`upgrade-card ${selectedUpgrade === 'one-bureau' ? 'selected' : ''}`}
                onClick={() => handleUpgradeSelect('one-bureau')}
                data-testid="upgrade-one-bureau"
              >
                <div className="upgrade-card-header">
                  <h3 className="upgrade-name">Add 1 Bureau</h3>
                </div>
                <div className="upgrade-price">
                  <span className="price-add">+$10</span>
                  <span className="price-total">$39.99 total</span>
                </div>
                <p className="upgrade-desc">Add TransUnion OR Equifax for improved accuracy</p>
                <button className="upgrade-card-btn">
                  Add 1 Bureau
                </button>
              </div>

              {/* Option 3: All 3 Bureaus - RECOMMENDED */}
              <div 
                className={`upgrade-card recommended ${selectedUpgrade === 'all-bureaus' ? 'selected' : ''}`}
                onClick={() => handleUpgradeSelect('all-bureaus')}
                data-testid="upgrade-all-bureaus"
              >
                <div className="recommended-badge">MOST RECOMMENDED</div>
                <div className="upgrade-card-header">
                  <h3 className="upgrade-name">Add TransUnion + Equifax</h3>
                </div>
                <div className="upgrade-price">
                  <span className="price-add">+$20</span>
                  <span className="price-total">$49.99 total</span>
                </div>
                <p className="upgrade-desc">Full 3-bureau plan for maximum accuracy</p>
                <ul className="upgrade-benefits">
                  <li>✓ See what more lenders see</li>
                  <li>✓ Most accurate recommendations</li>
                  <li>✓ Complete credit picture</li>
                </ul>
                <button className="upgrade-card-btn primary">
                  Unlock Full 3-Bureau Plan
                </button>
              </div>
            </div>

            {/* Beta Code for Pro */}
            <div className="upsell-beta-section">
              <div className="beta-divider-small">
                <span>OR</span>
              </div>
              <div className="beta-pro-box">
                <label className="beta-pro-label">Have a beta code?</label>
                <div className="beta-input-row">
                  <input
                    type="text"
                    placeholder="ENTER CODE"
                    value={betaCode}
                    onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                    className="beta-pro-input"
                    data-testid="beta-pro-input"
                  />
                  <button
                    onClick={handleBetaUpgrade}
                    disabled={betaLoading}
                    className="beta-pro-btn"
                    data-testid="beta-pro-submit"
                  >
                    {betaLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {betaError && <p className="beta-error-msg">{betaError}</p>}
              </div>
            </div>

            {/* Skip Option */}
            <button
              className="upsell-skip-btn"
              onClick={handleSkip}
              data-testid="skip-upsell-btn"
            >
              No thanks, continue to my dashboard →
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default UpsellPage;
